// Lytx Camera Domain Configuration
export const lytxCameraConfig = {
  // Basic domain information
  id: 'lytx-camera',
  name: 'Lytx Camera',
  tagline: 'Professional Fleet Camera Solutions by Lytx',
  description: 'Advanced AI-powered fleet cameras and video management solutions for enhanced safety and performance.',
  
  // Assets
  logo: '/assets/logos/lytx-camera-logo.png',
  whiteLogo: '/assets/logos/lytx-camera-white-logo.png',
  favicon: '/assets/favicons/lytx-camera-favicon.png',
  icon: '/assets/icons/lytx-camera',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
            
  // Shop link
  shopLink: 'https://shop.fleethoster.com/products/dcc07-12',

  // UI Configuration
  variant: 'elegant',
  showFeatures: false,
  showTestimonials: false,
  
  // Content sections
  hero: {
    title: 'Lytx Camera',
    subtitle: 'Get the video safety and professional dash cam solution you need to manage and protect your most valuable assets in motion—your people, your equipment, and your reputation.',
    features: ['AI-Powered Analytics', 'Real-time Monitoring', 'Driver Coaching', 'Cloud Storage']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'Lytx Camera Gallery',
    subtitle: 'Explore our professional AI-powered fleet camera and video management solutions',
    description: 'See how Lytx Camera transforms fleet safety with advanced video technology and AI analytics'
  },

  features: {
    title: 'Lytx Camera Features',
    subtitle: 'Professional AI-powered fleet cameras with comprehensive safety solutions.',
    description: 'Lytx Camera solutions help protect your most valuable assets while transforming your fleet operations with advanced AI technology.',
    items: [
      {
        title: 'AI-Powered Video Analytics',
        description: 'Advanced artificial intelligence analyzes video footage to identify risky driving behaviors and safety incidents.',
        icon: '🤖'
      },
      {
        title: 'Real-time Fleet Monitoring',
        description: 'Monitor your entire fleet in real-time with high-definition video and instant alerts.',
        icon: '📹'
      },
      {
        title: 'Driver Coaching & Training',
        description: 'Improve driver performance with personalized coaching based on AI-identified behaviors.',
        icon: '👨‍🏫'
      },
      {
        title: 'Incident Management',
        description: 'Streamlined incident reporting and analysis to reduce risk and improve fleet safety.',
        icon: '🚨'
      },
      {
        title: 'Cloud Video Storage',
        description: 'Secure cloud storage with easy access to video footage for compliance and training.',
        icon: '☁️'
      },
      {
        title: 'Fleet Performance Analytics',
        description: 'Comprehensive analytics and reporting to optimize fleet operations and reduce costs.',
        icon: '📊'
      }
    ]
  },
  
  solutions: {
    title: 'Lytx Camera Solutions',
    subtitle: 'Professional fleet camera solutions tailored to your business needs',
    description: 'Discover our comprehensive range of Lytx camera solutions designed to enhance fleet safety, improve driver performance, and optimize operations.',
    sections: [
      {
        title: 'AI-Powered Fleet Safety Solutions',
        description: 'Lytx Camera solutions use advanced artificial intelligence to analyze driver behavior, identify risks, and provide actionable insights to improve fleet safety and performance.',
      },
      {
        title: 'Professional Fleet Camera Systems',
        description: 'High-definition camera systems designed for commercial fleets with real-time monitoring, cloud storage, and comprehensive video management capabilities.',
      },
      {
        title: 'Driver Performance & Coaching',
        description: 'Transform driver behavior with AI-powered coaching tools that identify improvement opportunities and provide personalized training recommendations.',
      }
    ]
  },

  testimonials: [
    {
      content: "Lytx Camera has revolutionized our fleet safety program. The AI-powered insights have helped us reduce incidents by 40% and improve driver performance significantly.",
      author: "Sarah Johnson",
      role: "Fleet Safety Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg"
    },
    {
      content: "The advanced video analytics and driver coaching features have transformed how we manage our fleet. Lytx Camera is an essential tool for modern fleet operations.",
      author: "Michael Rodriguez",
      role: "Operations Director",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/44.jpg"
    }
  ],

  blog: {
    title: 'Lytx Camera Blog',
    subtitle: 'Fleet Safety & Camera Technology Insights',
    description: 'Stay informed with the latest news, insights, and best practices in fleet safety, AI-powered video analytics, and driver performance optimization.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'Lytx Camera - Professional AI-Powered Fleet Camera Solutions',
    description: 'Enhance fleet safety with Lytx AI-powered cameras, real-time monitoring, driver coaching, and video analytics for improved fleet performance.',
    keywords: [
      'lytx camera', 'lytx fleet camera', 'fleet camera solutions', 'AI-powered cameras', 'fleet safety cameras', 'driver coaching', 'video analytics',
      'fleet monitoring', 'commercial dashcam', 'fleet video management', 'lytx camera systems', 'fleet safety technology', 'driver performance',
      'fleet camera analytics', 'commercial vehicle cameras', 'fleet video storage', 'lytx camera technology', 'fleet safety solutions'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'Lytx Camera - Professional AI-Powered Fleet Camera Solutions',
    ogDescription: 'Transform your fleet safety with Lytx advanced AI-powered camera solutions. Real-time monitoring, driver coaching, and video analytics.',
    ogType: 'website',
    ogUrl: 'https://lytx.camera',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'Lytx Camera - Professional AI-Powered Fleet Camera Solutions',
    twitterDescription: 'Transform your fleet safety with Lytx advanced AI-powered camera solutions. Real-time monitoring, driver coaching, and video analytics.',
    
    // Robots
    robots: 'index, follow',
    author: 'Lytx Camera',
    publisher: 'Lytx Camera',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'Lytx Camera - Professional AI-Powered Fleet Camera Solutions',
        description: 'Enhance fleet safety with Lytx AI-powered cameras, real-time monitoring, driver coaching, and video analytics for improved fleet performance.',
        keywords: ['lytx camera', 'fleet camera solutions', 'AI-powered cameras', 'fleet safety cameras', 'driver coaching']
      },
      solutions: {
        title: 'Lytx Camera Solutions - Professional Fleet Camera Systems',
        description: 'Comprehensive fleet camera solutions with AI-powered analytics, driver coaching, and video management for enhanced fleet safety and performance.',
        keywords: ['lytx camera solutions', 'fleet camera systems', 'AI video analytics', 'driver coaching', 'fleet safety technology']
      },
      blog: {
        title: 'Lytx Camera Blog | Fleet Safety & Technology Insights',
        description: 'Latest news, insights, and best practices for fleet safety, AI-powered video analytics, and driver performance optimization.',
        keywords: ['lytx camera blog', 'fleet safety insights', 'camera technology', 'driver performance', 'fleet safety best practices']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
